import PropTypes from 'prop-types';
// @mui
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';

// eslint-disable-next-line consistent-return
const generateHandler = (filters, onFilters, option) => (newValue, interactionType) => {
  if (option === 'experience') {
    return onFilters('experience', newValue);
  }

  if (interactionType === 'checkbox') {
    const updatedFilters = filters[option]?.includes(newValue)
      ? filters[option].filter((value) => value !== newValue)
      : [...(filters[option] || []), newValue];
    onFilters(option, updatedFilters);
  } else {
    onFilters(option, newValue);
  }
};

const renderFilterSection = (title, options, selectedValues, onChangeHandler, interactionType) => {
  const isCheckbox = options.length <= 5;

  return (
    <Stack>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {isCheckbox ? (
        options.map((option) => (
          <FormControlLabel
            key={option}
            control={
              <Checkbox
                checked={selectedValues.includes(option)}
                onClick={() => onChangeHandler(option, interactionType)}
              />
            }
            label={option}
          />
        ))
      ) : (
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={options}
          value={selectedValues}
          onChange={(event, newValue) => onChangeHandler(newValue, interactionType)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                selectedValues?.length === 0 && !params?.inputProps?.value
                  ? `Select ${title}`
                  : ''
              }
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              {option}
            </li>
          )}
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                size="small"
                variant="soft"
              />
            ))
          }
        />
      )}
    </Stack>
  );
};

export default function CandListFilter({
  open,
  onOpen,
  onClose,
  filters,
  onFilters,
  canReset,
  onResetFilters,
  genderOpt,
  srcHireOpt,
  candStatusOpt,
  candCityOpt,
  candSklOpt,
  candEmailOpt,
  candNmOpt,
  experienceOpt,
  statusOptions,
}) {
  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 2, pr: 1, pl: 2.5 }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Filters
      </Typography>

      <Tooltip title="Reset">
        <IconButton onClick={onResetFilters}>
          <Badge color="error" variant="dot" invisible={!canReset}>
            <Iconify icon="solar:restart-bold" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Tooltip title="Close">
        <IconButton onClick={onClose}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
  return (
    <>
      <Button
        disableRipple
        color="inherit"
        endIcon={
          <Badge color="error" variant="dot" invisible={!canReset}>
            <Iconify icon="ic:round-filter-list" />
          </Badge>
        }
        onClick={onOpen}
      >
        Filters
      </Button>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 280 },
        }}
      >
        {renderHead}
        <Divider />
        <Scrollbar sx={{ px: 2.5, py: 5 }}>
          <Stack spacing={5}>
            {renderFilterSection(
              'Status',
              statusOptions,
              filters.Status,
              generateHandler(filters, onFilters, 'Status'),
              statusOptions.length <= 5 ? 'checkbox' : 'autocomplete'
            )}
            {renderFilterSection(
              'Candidate Name',
              candNmOpt,
              filters.fname,
              generateHandler(filters, onFilters, 'fname'),
              candNmOpt.length <= 5 ? 'checkbox' : 'autocomplete'
            )}
            {renderFilterSection(
              'Candidate Email',
              candEmailOpt,
              filters.email,
              generateHandler(filters, onFilters, 'email'),
              candEmailOpt.length <= 5 ? 'checkbox' : 'autocomplete'
            )}
            {renderFilterSection(
              'Primary Skills',
              candSklOpt,
              filters.primarySkill,
              generateHandler(filters, onFilters, 'primarySkill'),
              candSklOpt.length <= 5 ? 'checkbox' : 'autocomplete'
            )}
            {renderFilterSection(
              'Gender',
              genderOpt,
              filters.gender,
              generateHandler(filters, onFilters, 'gender'),
              genderOpt.length <= 5 ? 'checkbox' : 'autocomplete'
            )}
            <Stack>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', pb: '17px' }}>
                Experience
              </Typography>

              <Stack direction="row" spacing={0.5} sx={{ mt: 0 }}>
                <TextField
                  label="Experience From"
                  type="number"
                  value={filters.experience?.from || ''}
                  onChange={(e) =>
                    onFilters('experience', { ...filters.experience, from: e.target.value })
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Experience To"
                  type="number"
                  value={filters.experience?.to || ''}
                  onChange={(e) =>
                    onFilters('experience', { ...filters.experience, to: e.target.value })
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Stack>

            {renderFilterSection(
              'Source Of Hire',
              srcHireOpt,
              filters.sourceOfHire,
              generateHandler(filters, onFilters, 'sourceOfHire'),
              srcHireOpt.length <= 5 ? 'checkbox' : 'autocomplete'
            )}
            {renderFilterSection(
              'Candidate Status',
              candStatusOpt,
              filters.candStatus,
              generateHandler(filters, onFilters, 'candStatus'),
              candStatusOpt.length <= 5 ? 'checkbox' : 'autocomplete'
            )}
            {renderFilterSection(
              'Current City',
              candCityOpt,
              filters.city,
              generateHandler(filters, onFilters, 'city'),
              candCityOpt.length <= 5 ? 'checkbox' : 'autocomplete'
            )}

            {renderFilterSection(
              'Candidate Form Status',
              ['Complete', 'Incomplete'], // Define selectable options
              filters.formStatus || '', // Default to empty string
              generateHandler(filters, onFilters, 'formStatus'),
              'checkbox'
            )}
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}
CandListFilter.propTypes = {
  canReset: PropTypes.bool,
  filters: PropTypes.object,
  onClose: PropTypes.func,
  onFilters: PropTypes.func,
  onOpen: PropTypes.func,
  onResetFilters: PropTypes.func,
  open: PropTypes.bool,
  genderOpt: PropTypes.array,
  experienceOpt: PropTypes.array,
  srcHireOpt: PropTypes.array,
  candStatusOpt: PropTypes.array,
  candCityOpt: PropTypes.array,
  candSklOpt: PropTypes.array,
  candNmOpt: PropTypes.array,
  candEmailOpt: PropTypes.array,
  statusOptions: PropTypes.array,
};
